import './perfectagel-calculator.scss';
import {
	REGIONS,
	ROUTES,
	compute,
	fmt,
	num,
	runAcceptanceTests,
} from './engine.js';

const reduce = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

const qs = ( root, s ) => root.querySelector( s );
const qsa = ( root, s ) => Array.from( root.querySelectorAll( s ) );

const tweens = new WeakMap();

function setValue( el, target, decimals ) {
	const text = fmt( target, decimals );
	if ( reduce ) {
		el.textContent = text;
		return;
	}
	const prev = tweens.get( el );
	const from = prev && Number.isFinite( prev.to ) ? prev.to : target;
	if ( prev && prev.raf ) {
		cancelAnimationFrame( prev.raf );
	}
	if ( from === target ) {
		el.textContent = text;
		tweens.set( el, { to: target } );
		return;
	}
	const t0 = performance.now();
	const dur = 420;
	const rec = { to: target, raf: 0 };
	const step = ( now ) => {
		const p = Math.min( 1, ( now - t0 ) / dur );
		const e = 1 - Math.pow( 1 - p, 3 );
		el.textContent = fmt( from + ( target - from ) * e, decimals );
		if ( p < 1 ) {
			rec.raf = requestAnimationFrame( step );
		} else {
			el.textContent = text;
			rec.raf = 0;
		}
	};
	rec.raf = requestAnimationFrame( step );
	tweens.set( el, rec );
}

function mailtoHref( state, c, mailTo ) {
	const R = c.R;
	const isFS = state.route === 'FS';
	const scenTxt = ROUTES[ state.route ].scenarios[ state.scenario ].label + ' ' + state.scenario;
	const L = [
		'Hello Avebe team,',
		'',
		'Here is my PerfectaGEL savings calculation from the online calculator.',
		'',
		'Product route: PerfectaGEL ' + state.route,
		'Region: ' + ( state.region === 'EU' ? 'EU (EUR)' : 'US (USD)' ),
		'',
		'My process:',
		'- Current drying time: ' + fmt( c.dry ) + ' h',
		'- Volume per drying cycle: ' + fmt( state.vol ) + ' ' + R.mass,
		'- Cabinet power: ' + fmt( state.kw ) + ' kW',
		'- Electricity price: ' + R.cur + num( state.price ).toFixed( 2 ) + ' per kWh',
		'- Scenario: ' + scenTxt,
	];
	if ( state.annual ) {
		L.push( '- Cycles: ' + fmt( state.cycles ) + ' per week x ' + fmt( state.weeks ) + ' weeks = ' + fmt( c.cyclesYear ) + ' per year' );
		L.push( '- Drying cabinets: ' + fmt( c.cabinets ) );
	}
	L.push(
		'',
		'Per drying cycle:',
		'- Extra capacity: ' + fmt( c.capCycle ) + ' ' + R.mass,
		'- Energy cost saved: ' + R.cur + fmt( c.costCycle ) + ' (' + fmt( c.kwhCycle ) + ' kWh)',
		'- CO2 reduction: ' + fmt( c.co2Cycle ) + ' ' + R.mass,
		( ! isFS && R.isetModel === 'throughput' ? '- Throughput: ' + fmt( c.throughput, 1 ) + 'x' : '- New drying time: ' + fmt( c.newDry ) + ' h' ),
		'- Cabinet hours freed: ' + fmt( c.hoursFreed ) + ' h',
	);
	if ( state.annual ) {
		L.push(
			'',
			'Per year (' + fmt( c.cyclesYear ) + ' cycles' + ( c.cabinets > 1 ? ' x ' + fmt( c.cabinets ) + ' cabinets' : '' ) + '):',
			'- Extra capacity: ' + fmt( c.capYear, 1 ) + ' ' + R.big,
			'- Energy cost saved: ' + R.cur + fmt( c.costYear ),
			'- CO2 reduction: ' + fmt( c.co2Year, 1 ) + ' ' + R.big,
		);
	}
	L.push( '', 'Please send me more information about PerfectaGEL for my line.', '', 'Best regards,' );
	return 'mailto:' + mailTo
		+ '?subject=' + encodeURIComponent( 'My PerfectaGEL savings calculation' )
		+ '&body=' + encodeURIComponent( L.join( '\r\n' ) );
}

function renderPrint( state, c, mailTo, printEl ) {
	const R = c.R;
	const isFS = state.route === 'FS';
	const scenTxt = ROUTES[ state.route ].scenarios[ state.scenario ].label + ' ' + state.scenario;
	const tile = ( v, l ) => `<div><b>${ v }</b><span>${ l }</span></div>`;
	const year = state.annual ? `
    <div class="pg-print-sec">
      <h2>Per year${ c.cabinets > 1 ? ' · whole plant' : '' }</h2>
      <ul class="pg-print-list">
        <li>${ fmt( c.cyclesYear ) } cycles per year (${ fmt( state.cycles ) } per week × ${ fmt( state.weeks ) } weeks)${ c.cabinets > 1 ? ' × ' + fmt( c.cabinets ) + ' drying cabinets' : '' }</li>
      </ul>
      <div class="pg-print-grid">
        ${ tile( fmt( c.capYear, 1 ) + ' ' + R.big, 'extra capacity / year' ) }
        ${ tile( R.cur + fmt( c.costYear ), fmt( c.kwhYear ) + ' kWh saved / year' ) }
        ${ tile( fmt( c.co2Year, 1 ) + ' ' + R.big, 'CO₂ avoided / year' ) }
      </div>
    </div>` : '';

	printEl.innerHTML = `
    <div class="pg-print-top">
      <h1>PerfectaGEL® ${ state.route } · Business case</h1>
      <span>Royal Avebe · savings calculator</span>
    </div>
    <div class="pg-print-sec">
      <h2>My process</h2>
      <ul class="pg-print-list">
        <li>Current drying time: <b>${ fmt( c.dry ) } h</b> · new drying time: <b>${ fmt( c.newDry ) } h</b></li>
        <li>Volume per drying cycle: <b>${ fmt( state.vol ) } ${ R.mass }</b> · cabinet power: <b>${ fmt( state.kw ) } kW</b></li>
        <li>Electricity price: <b>${ R.cur }${ num( state.price ).toFixed( 2 ) } / kWh</b></li>
        <li>Scenario: <b>${ scenTxt }</b> · region: <b>${ state.region === 'EU' ? 'EU (EUR)' : 'US (USD)' }</b></li>
      </ul>
    </div>
    <div class="pg-print-sec">
      <h2>Per drying cycle</h2>
      <div class="pg-print-grid">
        ${ tile( '+' + fmt( c.capCycle ) + ' ' + R.mass, 'extra capacity' ) }
        ${ tile( R.cur + fmt( c.costCycle ), fmt( c.kwhCycle ) + ' kWh saved' ) }
        ${ tile( fmt( c.co2Cycle ) + ' ' + R.mass, 'CO₂ avoided' ) }
      </div>
      <p class="pg-print-note">${ fmt( c.hoursFreed ) } cabinet hours freed per cycle${
	! isFS && R.isetModel === 'throughput' ? ' · throughput ' + fmt( c.throughput, 1 ) + '×' : '' }.</p>
    </div>
    ${ year }
    <div class="pg-print-sec">
      <h2>Assumptions</h2>
      <ul class="pg-print-list">
        <li>Extra capacity = ${ isFS
		? 'volume per cycle × drying time reduction'
		: ( R.isetModel === 'throughput'
			? 'volume per cycle × hours freed ÷ new drying time'
			: 'volume per cycle × hours freed ÷ current drying time' ) }</li>
        <li>Energy saving = cabinet hours freed × cabinet kW × electricity price</li>
        <li>CO₂ reduction = kWh saved × grid factor</li>
        <li>${ R.co2Disclaimer }</li>
      </ul>
      <p class="pg-print-note">${ ROUTES[ state.route ].disclaimer } Product cost and net saving: your Avebe contact
        calculates the full cost-in-use picture with you in the tailored evaluation. Indicative figures only;
        not a commercial offer or technical guarantee.</p>
    </div>
    <div class="pg-print-foot">
      <span>Royal Avebe · Confectionery · PerfectaGEL® · avebe.com</span>
      <span>${ mailTo }</span>
    </div>`;
}

function initCalculator( root ) {
	if ( ! root || root.dataset.pgReady === '1' ) {
		return;
	}
	root.dataset.pgReady = '1';

	const wrap = root.closest( '.o-perfectagelCalculator' ) || root.parentElement;
	const $ = ( s ) => qs( wrap, s ) || qs( root, s );
	const $$ = ( s ) => {
		const inWrap = wrap ? qsa( wrap, s ) : [];
		return inWrap.length ? inWrap : qsa( root, s );
	};

	const mailTo = root.dataset.mailto || 'perfectagel@avebe.com';
	const printEl = wrap.querySelector( '.pg-print' );
	if ( printEl && printEl.parentElement !== document.body ) {
		document.body.appendChild( printEl );
	}

	const state = {
		region: 'EU',
		route: 'FS',
		scenario: 'Conservative',
		dry: 48,
		vol: 2000,
		kw: 20,
		price: 0.15,
		cycles: 5,
		weeks: 50,
		cabinets: 1,
		annual: false,
	};

	const val = ( key, n, d = 0 ) => $$( `[data-val="${ key }"]` ).forEach( ( el ) => setValue( el, n, d ) );

	function renderScenarioRange( c ) {
		const R = c.R;
		const rows = Object.keys( ROUTES[ state.route ].scenarios ).map( ( key ) => {
			const s = compute( state, key );
			return { key, label: ROUTES[ state.route ].scenarios[ key ].label, value: s.costYear };
		} );
		const peak = Math.max( ...rows.map( ( r ) => r.value ), 1 );
		$( '#scenRangeTitle' ).textContent = 'Yearly energy saving by scenario'
			+ ( c.cabinets > 1 ? ' · ' + fmt( c.cabinets ) + ' cabinets' : '' );

		const box = $( '#scenBars' );
		if ( box.children.length !== rows.length ) {
			box.innerHTML = rows.map( ( r ) => `
      <button type="button" class="pg-srbar" data-scen="${ r.key }">
        <span class="pg-srbar-top">
          <span class="pg-srbar-tag">Selected</span>
          <span class="pg-srbar-val"></span>
        </span>
        <span class="pg-srbar-track"><span class="pg-srbar-fill"></span></span>
        <span class="pg-srbar-cap"><b></b> <span></span></span>
      </button>` ).join( '' );
		}
		rows.forEach( ( r, i ) => {
			const b = box.children[ i ];
			b.dataset.scen = r.key;
			b.setAttribute( 'aria-pressed', String( r.key === state.scenario ) );
			b.querySelector( '.pg-srbar-val' ).textContent = R.cur + fmt( r.value );
			b.querySelector( '.pg-srbar-fill' ).style.height = Math.max( 3, r.value / peak * 100 ) + '%';
			b.querySelector( '.pg-srbar-cap b' ).textContent = r.label;
			b.querySelector( '.pg-srbar-cap span' ).textContent = r.key;
		} );
	}

	function render() {
		const c = compute( state );
		const R = c.R;
		const isFS = state.route === 'FS';
		root.dataset.route = state.route;
		root.dataset.region = state.region;

		$$( '[data-unit-mass]' ).forEach( ( e ) => { e.textContent = R.mass; } );
		$$( '[data-unit-big]' ).forEach( ( e ) => { e.textContent = R.big; } );
		$$( '[data-unit-cur]' ).forEach( ( e ) => { e.textContent = R.cur; } );
		$( '#unitVol' ).textContent = R.mass;
		$( '#unitPrice' ).textContent = R.cur + ' / kWh';
		$( '#hintPrice' ).textContent = R.priceHint;
		$( '#hintDry' ).textContent = ROUTES[ state.route ].dryHint;
		$( '#co2Note' ).textContent = R.co2Note;
		$( '#scenLabel' ).textContent = ROUTES[ state.route ].scenLabel;
		$( '#trialNote' ).textContent = ROUTES[ state.route ].trial;
		$( '#routeNote' ).textContent = ROUTES[ state.route ].note;
		$( '#claim' ).firstElementChild.innerHTML = ROUTES[ state.route ].claim;
		$( '#capNote' ).textContent = isFS ? ROUTES.FS.capNote : R.isetCapNote;

		const card = $( `.pg-route[data-for="${ state.route }"]` );
		$( '#routeDetailText' ).textContent = card.querySelector( '.pg-route-desc' ).textContent;
		$( '#liveRoute' ).textContent = 'PerfectaGEL® ' + state.route;
		$( '#disclaimer' ).textContent =
			ROUTES[ state.route ].disclaimer + ' ' + R.co2Disclaimer
			+ ' This estimator provides indicative figures only and does not constitute a commercial offer or technical guarantee.';

		Object.entries( ROUTES[ state.route ].scenarios ).forEach( ( [ k, v ] ) => {
			const el = $( `[data-scen-val="${ k }"]` );
			if ( el ) {
				el.textContent = v.label;
			}
		} );
		const sl = $( '#volSlider' );
		sl.min = R.volume.min;
		sl.max = R.volume.max;
		sl.step = R.volume.step;
		sl.value = Math.min( Math.max( state.vol, R.volume.min ), R.volume.max );
		sl.style.setProperty( '--fill', ( ( sl.value - sl.min ) / ( sl.max - sl.min ) * 100 ) + '%' );

		$( '#summary' ).innerHTML = isFS
			? '<b>' + ROUTES.FS.scenarios[ state.scenario ].label + ' drying time</b> · new drying time '
				+ fmt( c.newDry ) + ' h'
			: '<b>' + fmt( c.dry ) + ' h → ' + fmt( c.newDry ) + ' h</b> · drying time per cycle';

		const total = Math.max( c.dry, c.newDry, 1 );
		const pctNew = Math.max( 0, Math.min( 100, c.newDry / total * 100 ) );
		$( '#barNew' ).style.width = pctNew + '%';
		$( '#barFreed' ).style.width = ( 100 - pctNew ) + '%';
		$( '#barTrack' ).style.setProperty( '--tick', ( 600 / Math.max( total, 1 ) ) + '%' );
		$( '#barNewTag' ).textContent = pctNew > 16 ? fmt( c.newDry, 0 ) + ' h drying' : '';
		$( '#barFreedTag' ).textContent = pctNew < 84 ? fmt( c.hoursFreed, 0 ) + ' h freed' : '';
		$( '#legendTotal' ).textContent = fmt( c.dry ) + ' h';
		$( '#legendFreed' ).innerHTML = 'Cabinet hours freed <b>' + fmt( c.hoursFreed ) + ' h</b>';
		$( '#barA11y' ).textContent = 'One drying cycle of ' + fmt( c.dry ) + ' hours: '
			+ fmt( c.newDry ) + ' hours drying, ' + fmt( c.hoursFreed ) + ' cabinet hours freed.';

		val( 'capCycle', c.capCycle );
		val( 'costCycle', c.costCycle );
		val( 'kwhCycle', c.kwhCycle );
		val( 'co2Cycle', c.co2Cycle );
		if ( state.annual ) {
			val( 'cyclesYear', c.cyclesYear );
			val( 'capYear', c.capYear, 1 );
			val( 'costYear', c.costYear );
			val( 'kwhYear', c.kwhYear );
			val( 'co2Year', c.co2Year, 1 );
			$( '#annualTitle' ).textContent = c.cabinets > 1 ? 'Annual estimate · whole plant' : 'Annual estimate';
			$( '#annualCabs' ).textContent = c.cabinets > 1 ? ' · ' + fmt( c.cabinets ) + ' cabinets' : '';
			$( '#capYearSub' ).textContent = 'from ' + fmt( c.capCycle ) + ' ' + R.mass + ' per cycle'
				+ ( c.cabinets > 1 ? ' × ' + fmt( c.cabinets ) + ' cabinets' : '' );
			$( '#co2YearSub' ).textContent = 'at ' + R.co2 + ' ' + R.mass + ' CO₂ / kWh';
			renderScenarioRange( c );
		}

		const liveYear = $( '#liveYear' );
		liveYear.textContent = state.annual ? R.cur + fmt( c.costYear ) + ' per year' : '';
		liveYear.classList.toggle( 'pg-off', ! state.annual );

		$( '#howCap' ).textContent = isFS
			? 'volume per cycle × drying time reduction (' + ROUTES.FS.scenarios[ state.scenario ].label.replace( '−', '' ) + ' of your batch)'
			: ( R.isetModel === 'throughput'
				? 'volume per cycle × cabinet hours freed ÷ new drying time — the extra volume the same cabinet can now dry'
				: 'volume per cycle × cabinet hours freed ÷ current drying time — the share of the cycle that comes free' );
		$( '#howEnergy' ).textContent = 'cabinet hours freed × cabinet power (kW) × your electricity price';
		$( '#howCo2' ).textContent = 'kWh saved × the electricity grid CO₂ factor for your region';
		$( '#howSrc' ).textContent = 'Grid factors: 0.233 kg CO₂ / kWh (Eurostat 2024, EU) · 0.81 lb CO₂ / kWh (EIA 2023, US). '
			+ 'Scenarios follow the published Avebe performance ranges; everything else is your own input.';

		const chips = [];
		if ( ! isFS && R.isetModel === 'throughput' ) {
			chips.push( [ 'Throughput', fmt( c.throughput, 1 ) + '×' ] );
		} else {
			chips.push( [ 'New drying time', fmt( c.newDry ) + ' h' ] );
		}
		chips.push( [ 'Cabinet hours freed', fmt( c.hoursFreed ) + ' h' ] );
		chips.push( [ 'Energy', fmt( c.kwhCycle ) + ' kWh / cycle' ] );
		$( '#chips' ).innerHTML = chips
			.map( ( [ k, v ] ) => `<span class="pg-chip">${ k } <b>${ v }</b></span>` ).join( '' );

		const warn = $( '#warn' );
		if ( ! isFS && c.hoursFreed <= 0 ) {
			$( '#warnText' ).textContent = 'Your current drying time is already at or below the '
				+ ROUTES.ISET.scenarios[ state.scenario ].label
				+ ' ISET drying time, so there is nothing left to save. Raise your current drying time or pick a shorter scenario.';
			warn.classList.add( 'pg-on' );
		} else {
			warn.classList.remove( 'pg-on' );
		}

		$( '#srLive' ).textContent = 'Per cycle: ' + fmt( c.capCycle ) + ' ' + R.mass + ' extra capacity, '
			+ R.cur + fmt( c.costCycle ) + ' energy saved, ' + fmt( c.co2Cycle ) + ' ' + R.mass + ' CO₂ avoided.';

		$( '#mailBtn' ).dataset.href = mailtoHref( state, c, mailTo );
		if ( printEl ) {
			renderPrint( state, c, mailTo, printEl );
		}
	}

	function setAnnual( on, { focus = false } = {} ) {
		state.annual = on;
		const band = $( '#annualBand' );
		const optin = $( '#annualOptIn' );
		const field = $( '#annualField' );
		band.classList.toggle( 'pg-annual-off', ! on );
		$( '#scenRange' ).classList.toggle( 'pg-annual-off', ! on );
		optin.classList.toggle( 'pg-optin-off', on );
		field.classList.toggle( 'pg-on', on );
		if ( on && ! reduce ) {
			band.classList.remove( 'pg-reveal' );
			void band.offsetWidth;
			band.classList.add( 'pg-reveal' );
			field.classList.remove( 'pg-reveal' );
			void field.offsetWidth;
			field.classList.add( 'pg-reveal' );
		}
		render();
		syncLive();
		if ( on && focus ) {
			$( '#inCycles' ).focus( { preventScroll: true } );
		}
		if ( ! on && focus ) {
			$( '#annualOnBtn' ).focus( { preventScroll: true } );
		}
	}

	function moveThumb( segId, thumbId ) {
		const seg = $( segId );
		const thumb = $( thumbId );
		const labels = Array.from( seg.querySelectorAll( 'label' ) );
		const i = labels.findIndex( ( l ) => l.querySelector( 'input' ).checked );
		const w = 100 / labels.length;
		thumb.style.width = `calc(${ w }% - ${ 3 - 3 / labels.length }px)`;
		thumb.style.transform = `translateX(calc(${ i * 100 }% + ${ i * ( 3 / labels.length ) }px))`;
	}
	const syncThumbs = () => {
		moveThumb( '#regionSeg', '#regionThumb' );
		moveThumb( '#scenSeg', '#scenThumb' );
	};

	const readNum = ( el, fallback ) => {
		const v = parseFloat( el.value.replace( ',', '.' ) );
		return Number.isFinite( v ) ? v : fallback;
	};
	const bind = ( sel, key, min, max, format ) => {
		const el = $( sel );
		el.addEventListener( 'input', () => {
			let v = readNum( el, state[ key ] );
			if ( min !== undefined ) {
				v = Math.max( min, v );
			}
			if ( max !== undefined ) {
				v = Math.min( max, v );
			}
			state[ key ] = v;
			render();
		} );
		el.addEventListener( 'blur', () => {
			if ( format ) {
				el.value = format( state[ key ] );
				return;
			}
			if ( el.value === '' || ! Number.isFinite( parseFloat( el.value ) ) ) {
				el.value = state[ key ];
			}
		} );
	};
	bind( '#inDry', 'dry', 0.5, 200 );
	bind( '#inVol', 'vol', 0 );
	bind( '#inKw', 'kw', 0, 500 );
	bind( '#inPrice', 'price', 0, 5, ( v ) => num( v ).toFixed( 2 ) );
	bind( '#inCycles', 'cycles', 1, 21 );
	bind( '#inWeeks', 'weeks', 1, 52 );
	bind( '#inCabs', 'cabinets', 1, 99, ( v ) => String( Math.max( 1, Math.round( num( v ) ) ) ) );

	$$( '.pg-stepper-btn' ).forEach( ( btn ) => btn.addEventListener( 'click', () => {
		const next = Math.min( 99, Math.max( 1, Math.round( num( state.cabinets ) ) + Number( btn.dataset.step ) ) );
		state.cabinets = next;
		$( '#inCabs' ).value = next;
		render();
	} ) );

	$( '#volSlider' ).addEventListener( 'input', ( e ) => {
		state.vol = parseFloat( e.target.value );
		$( '#inVol' ).value = state.vol;
		render();
	} );

	$$( 'input[name="pg-scen"]' ).forEach( ( r ) => r.addEventListener( 'change', () => {
		state.scenario = r.value;
		syncThumbs();
		render();
	} ) );

	$$( 'input[name="pg-region"]' ).forEach( ( r ) => r.addEventListener( 'change', () => {
		state.region = r.value;
		applyDefaults();
		syncThumbs();
		render();
	} ) );

	$$( 'input[name="pg-route"]' ).forEach( ( r ) => r.addEventListener( 'change', () => {
		state.route = r.value;
		applyDefaults();
		render();
	} ) );

	function applyDefaults() {
		const d = REGIONS[ state.region ].defaults[ state.route ];
		state.dry = d.dry;
		state.vol = d.vol;
		state.kw = d.kw;
		state.price = d.price;
		$( '#inDry' ).value = d.dry;
		$( '#inVol' ).value = d.vol;
		$( '#inKw' ).value = d.kw;
		$( '#inPrice' ).value = d.price.toFixed( 2 );
	}

	$( '#annualOnBtn' ).addEventListener( 'click', () => setAnnual( true, { focus: true } ) );
	$( '#annualOffBtn' ).addEventListener( 'click', () => setAnnual( false, { focus: true } ) );

	$( '#presetBtn' ).addEventListener( 'click', () => {
		state.region = 'EU';
		state.scenario = 'Conservative';
		state.cycles = 5;
		state.weeks = 50;
		state.cabinets = 1;
		$( 'input[name="pg-region"][value="EU"]' ).checked = true;
		$( 'input[name="pg-scen"][value="Conservative"]' ).checked = true;
		$( '#inCycles' ).value = 5;
		$( '#inWeeks' ).value = 50;
		$( '#inCabs' ).value = 1;
		applyDefaults();
		syncThumbs();
		setAnnual( true );
	} );

	$( '#mailBtn' ).addEventListener( 'click', ( e ) => {
		window.location.href = e.currentTarget.dataset.href;
	} );

	$( '#printBtn' ).addEventListener( 'click', () => {
		document.body.classList.add( 'is-pg-print' );
		const cleanup = () => document.body.classList.remove( 'is-pg-print' );
		window.addEventListener( 'afterprint', cleanup, { once: true } );
		window.print();
		setTimeout( cleanup, 1000 );
	} );

	$( '#scenBars' ).addEventListener( 'click', ( e ) => {
		const btn = e.target.closest( '.pg-srbar' );
		if ( ! btn ) {
			return;
		}
		state.scenario = btn.dataset.scen;
		const radio = $( `input[name="pg-scen"][value="${ state.scenario }"]` );
		if ( radio ) {
			radio.checked = true;
		}
		syncThumbs();
		render();
	} );

	const scrollTo = ( el ) => el.scrollIntoView( { behavior: reduce ? 'auto' : 'smooth', block: 'start' } );
	$( '#jumpBtn' ).addEventListener( 'click', () => scrollTo( $( '.pg-calc' ) ) );

	const liveStrip = $( '#liveStrip' );
	$( '#liveBtn' ).addEventListener( 'click', () => scrollTo( $( '.pg-results' ) ) );

	const syncLive = ( () => {
		const form = $( '#pgForm' );
		const tiles = $( '.pg-results .pg-tiles' );
		let queued = false;
		const measure = () => {
			queued = false;
			const vh = window.innerHeight;
			const f = form.getBoundingClientRect();
			const t = tiles.getBoundingClientRect();
			const inputsOn = f.bottom > 0 && f.top < vh;
			const seenH = Math.min( t.bottom, vh ) - Math.max( t.top, 0 );
			const tilesOn = seenH > Math.min( t.height, vh ) * 0.5;
			liveStrip.classList.toggle( 'pg-on', inputsOn && ! tilesOn );
		};
		return () => {
			if ( ! queued ) {
				queued = true;
				requestAnimationFrame( measure );
			}
		};
	} )();
	window.addEventListener( 'scroll', syncLive, { passive: true } );
	window.addEventListener( 'resize', syncLive );
	syncLive();

	window.addEventListener( 'resize', syncThumbs );
	if ( document.fonts ) {
		document.fonts.ready.then( syncThumbs );
	}

	syncThumbs();
	render();

	window.pgAcceptanceTest = () => {
		const out = runAcceptanceTests();
		console.table( out );
		return out;
	};
}

const boot = () => {
	document.querySelectorAll( '.o-perfectagelCalculator .pg' ).forEach( initCalculator );
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', boot );
} else {
	boot();
}

if ( window.acf && typeof window.acf.addAction === 'function' ) {
	window.acf.addAction( 'render_block_preview/type=perfectagel-calculator', boot );
}

